import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase AUTH-S — Session expiry warning modal.
 *
 * Mounted once at the app root. The AuthContext flips `sessionWarning` to true
 * when the current JWT has ≤2 minutes remaining. The modal lets the user
 * either extend the session (calls /auth/refresh) or sign out immediately.
 * If they ignore the modal, the next protected request will 401 and the API
 * interceptor will redirect them to /login on its own.
 */
export function SessionExpiryWarning() {
    const { sessionWarning, setSessionWarning, refreshSession, logout, user } = useAuth();
    const navigate = useNavigate();
    const [remainingSec, setRemainingSec] = useState(120);
    const [busy, setBusy] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!sessionWarning) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return undefined;
        }
        // Read remaining from the token directly so the countdown stays
        // accurate even if the modal stays open across multiple ticks.
        const computeRemaining = () => {
            const tok = localStorage.getItem("rp_token");
            if (!tok) return 0;
            try {
                const parts = tok.split(".");
                const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
                const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
                const json = JSON.parse(atob(padded));
                if (typeof json.exp !== "number") return 0;
                return Math.max(0, Math.floor((json.exp * 1000 - Date.now()) / 1000));
            } catch {
                return 0;
            }
        };
        setRemainingSec(computeRemaining());
        intervalRef.current = setInterval(() => {
            setRemainingSec(computeRemaining());
        }, 1000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [sessionWarning]);

    if (!sessionWarning) return null;
    // Don't fire during impersonation — the impersonation banner owns that UX.
    if (user && user.impersonating) return null;

    const mm = String(Math.floor(remainingSec / 60)).padStart(1, "0");
    const ss = String(remainingSec % 60).padStart(2, "0");

    const onStay = async () => {
        setBusy(true);
        const ok = await refreshSession();
        setBusy(false);
        if (!ok) {
            // Refresh failed → session likely hit its 30d cap or was revoked.
            await logout();
            navigate("/login", { replace: true });
        }
    };

    const onSignOut = async () => {
        setBusy(true);
        await logout();
        setBusy(false);
        navigate("/login", { replace: true });
    };

    return (
        <Dialog
            open={sessionWarning}
            onOpenChange={(o) => { if (!o) setSessionWarning(false); }}
        >
            <DialogContent
                data-testid="session-expiry-warning-modal"
                className="max-w-md"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="text-slate-900">
                        Your session is about to expire
                    </DialogTitle>
                    <DialogDescription className="text-slate-600">
                        For your security, you'll be signed out automatically in{" "}
                        <span
                            data-testid="session-expiry-countdown"
                            className="font-semibold text-amber-700 tabular-nums"
                        >
                            {mm}:{ss}
                        </span>
                        . Stay signed in to keep working.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="outline"
                        onClick={onSignOut}
                        disabled={busy}
                        data-testid="session-expiry-signout-button"
                    >
                        Sign out
                    </Button>
                    <Button
                        onClick={onStay}
                        disabled={busy}
                        data-testid="session-expiry-stay-button"
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                    >
                        {busy ? "Extending…" : "Stay signed in"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SessionExpiryWarning;
